// candidate-card widget — α.5 of the outreach-agent slice.
//
// Renders inline in the outreach chat dock when the agent emits an
// `outreach.candidate.found` event AND a matching
// `:::widget[candidate-card]{...}:::` directive in its text reply. The
// card surfaces the candidate's identity (handle + score + one-sentence
// reason) and two founder-only actions: Create prospect / Reject.
//
// Both actions POST to projector routes:
//   POST /admin/outreach/candidates/{candidateId}/promote
//   POST /admin/outreach/candidates/{candidateId}/reject
//
// The projector is the single GCS writer (Invariant 3); the agent never
// emits prospect.discovered / outreach.candidate.promoted /
// outreach.candidate.rejected — those flow only from a founder click,
// which the outreach-event allowlist verifier enforces by rejecting any
// envelope with `__source: 'agent'` for those types.
//
// State machine (local-only — the dossier truth lives in the projector
// after the POST):
//   idle      → click promote → pending:'promote' → ok → decided:'promoted'
//             → click reject  → pending:'reject'  → ok → decided:'rejected'
//   On fetch failure pending clears + decided stays null so the founder
//   can retry. The card hides the buttons once decided so a stale dock
//   doesn't double-fire.
//
// Tokens: bg-card, border-border, text-foreground-mute — all real
// tailwind tokens declared in apps/web/src/styles/tailwind.css. No hex.

import { useState } from 'react';

interface Props {
  candidateId: string;
  handle: string;
  channel: string;
  name?: string;
  reason_line: string;
  preview_score: number | string;
  // Debug-only render hook so static tests can render the decided branch
  // without driving the click → fetch path (renderToStaticMarkup doesn't
  // run effects). Mirrors KycStripeIdentity's `state` prop pattern.
  decidedOverride?: 'promoted' | 'rejected';
}

export function CandidateCard(props: Props) {
  const {
    candidateId,
    handle,
    name,
    reason_line,
    preview_score,
    decidedOverride,
  } = props;
  const [pending, setPending] = useState<'promote' | 'reject' | null>(null);
  const [decided, setDecided] = useState<'promoted' | 'rejected' | null>(
    decidedOverride ?? null,
  );

  const act = async (kind: 'promote' | 'reject') => {
    setPending(kind);
    let res: Response | null = null;
    try {
      res = await fetch(
        `/admin/outreach/candidates/${candidateId}/${kind}`,
        {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({}),
        },
      );
    } catch {
      // Network failure — leave the card interactive so the founder
      // can retry. Same shape as the !res.ok branch below.
    }
    setPending(null);
    if (res && res.ok) {
      setDecided(kind === 'promote' ? 'promoted' : 'rejected');
    }
  };

  if (decided) {
    return (
      <div
        data-testid="candidate-card"
        data-state={decided}
        className="my-2 rounded-md border border-border bg-card px-3 py-2 text-sm text-foreground-mute"
      >
        Candidate {handle} {decided}.
      </div>
    );
  }

  return (
    <div
      data-testid="candidate-card"
      data-state="idle"
      className="my-2 rounded-md border border-border bg-card p-3"
    >
      <div className="flex items-center justify-between">
        <strong className="text-sm text-foreground">{name ?? handle}</strong>
        <span className="text-xs text-foreground-mute">
          score {preview_score}
        </span>
      </div>
      <div className="my-1 text-sm text-foreground">{reason_line}</div>
      <div className="mt-2 flex gap-2">
        <button
          type="button"
          data-testid="candidate-promote"
          onClick={() => act('promote')}
          disabled={!!pending}
          className="rounded-md bg-primary px-3 py-1 text-sm text-primary-foreground disabled:cursor-not-allowed disabled:opacity-40"
        >
          {pending === 'promote' ? 'Creating…' : 'Create prospect'}
        </button>
        <button
          type="button"
          data-testid="candidate-reject"
          onClick={() => act('reject')}
          disabled={!!pending}
          className="rounded-md border border-border bg-background px-3 py-1 text-sm text-foreground hover:bg-card disabled:cursor-not-allowed disabled:opacity-40"
        >
          {pending === 'reject' ? 'Rejecting…' : 'Reject'}
        </button>
      </div>
    </div>
  );
}
